import React, { memo, useMemo } from "react";
import { Icon } from "components/src/components/Icon/Icon";
import { Score } from "components-sbk/src/components/Score/Score";
import cx from "components/src/utils/cx";
import { useSync } from "shared/src/hooks/useSync";
import type { CompetitorVariantType } from "../types";
import type { SizeType } from "shared/src/types";
import type { SyncType } from "shared/src/hooks/useSync";

export type ScoreWithPossesionProps = {
  score: number | string;
  inverse: boolean;
  showAnimation: boolean;
  compact: boolean;
  sport: string;
  possession: boolean;
  variant: CompetitorVariantType;
  hasScore: boolean;
  size: SizeType;
  scoreStacked?: boolean;
  forcedReadableToken?: string;
  sync?: SyncType;
};

export const ScoreWithPossesion = memo<ScoreWithPossesionProps>((props) => {
  /**
   * Sync
   */
  const propsWithSync = useSync<ScoreWithPossesionProps>(props);
  const {
    score,
    inverse,
    showAnimation,
    compact,
    sport,
    possession,
    variant,
    hasScore,
    size,
    forcedReadableToken,
    scoreStacked,
  } = propsWithSync;
  const isVerticalVariant = variant === "vertical";
  const scoreNotEmpty = score !== undefined && score !== null;
  const _hasScore = hasScore || scoreNotEmpty;

  const possessionArrowProps = useMemo(() => {
    return {
      possession,
      inverse,
      hasScore,
      compact,
      isVerticalVariant,
      forcedReadableToken,
      scoreStacked
    };
  }, [
    possession,
    inverse,
    hasScore,
    compact,
    isVerticalVariant,
    forcedReadableToken,
    scoreStacked
  ]);

  return (
    <>
      {_hasScore ? (
        <div
          className={cx(
            {
              "cui-mr-2 cui-ml-1 md:cui-mr-4 md:cui-ml-2px":
                inverse && !isVerticalVariant,
              "-cui-order-2 cui-flex-grow":
                !compact && inverse && !isVerticalVariant,
              "cui-ml-2 cui-mr-1 md:cui-ml-4 md:cui-mr-2px":
                !inverse && !isVerticalVariant,
              "cui-justify-end": !inverse && !isVerticalVariant && Boolean(scoreStacked)
            },
            "cui-flex cui-items-center"
          )}
        >
          {scoreNotEmpty ? (
            <div className="cui-flex">
              {scoreStacked && !inverse ? <PossessionArrow {...possessionArrowProps} flip={true} /> : null }
              <Score
                score={score}
                showAnimation={showAnimation}
                sport={sport}
                size={size}
                variant={scoreStacked ? "minimal" : undefined}
              />
              {scoreStacked && inverse ? <PossessionArrow {...possessionArrowProps} flip={true} /> : null}
            </div>
          ) : null}
        </div>
      ) : null}
     {!scoreStacked ? <PossessionArrow {...possessionArrowProps} flip={false} /> : null }
    </>
  );
});

const PossessionArrow = ({
  possession,
  inverse,
  compact,
  hasScore,
  isVerticalVariant,
  forcedReadableToken,
  scoreStacked,
  flip
}) => {
  return (
    <div
      className={cx(
        {
          "cui-opacity-0": !possession,
          "cui-opacity-100": possession,
          // "-cui-ml-1": !inverse,
          "cui-order-first": inverse,
          "cui-ml-1": !inverse && !hasScore,
          "cui-order-last": inverse && compact && !isVerticalVariant,
        },
        `cui-flex cui-items-center cui-transition-opacity cui-duration-200 cui-select-none`
      )}
    >
      <Icon
        name={flip ? "icon_ui_triangle_sm_right" : "icon_ui_triangle_sm_left"}
        className={cx({
          "cui-rotate-180": inverse,
        })}
        size="md"
        color={
          forcedReadableToken && !scoreStacked
            ? forcedReadableToken.replace("cui-text-", "")
            : "highlight-accent"
        }
      />
    </div>
  );
};
